import { useMemo, useState, type Key } from 'react';
import { Alert, Button, Collapse, Empty, Spin, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { SceneSetCompareNode, TaskDiffMode } from '@apis/simulation';
import './SceneSetComparison.css';

type Row = Omit<SceneSetCompareNode, 'children'> & { children?: Row[] };

const toRows = (nodes: SceneSetCompareNode[]): Row[] => nodes.map((node) => ({
  ...node,
  children: node.children.length ? toRows(node.children) : undefined,
}));

const matrixColumns = [
  ['passPass', 'pass_pass', 'PassPass', '均通过'],
  ['passFail', 'pass_fail', 'PassFail', '新增失败'],
  ['failPass', 'fail_pass', 'FailPass', '新增通过'],
  ['failFail', 'fail_fail', 'FailFail', '均失败'],
] as const;

const makeColumns = (onSelect: Props['onSelect'], selectedNodeId?: number, selectedMode?: TaskDiffMode): ColumnsType<Row> => [
  { title: '场景集', dataIndex: 'name', width: 260 },
  {
    title: '场景数', width: 165, align: 'right',
    render: (_, row) => (
      <div className="scene-set-compare-counts">
        <div>{row.intersectionSceneCount} 共有</div>
        <div className="scene-set-compare-secondary">当前 {row.today.total} / 对比 {row.compare.total}</div>
        {(row.todayOnlySceneCount > 0 || row.compareOnlySceneCount > 0) && (
          <div className="scene-set-compare-secondary">
            仅当前 {row.todayOnlySceneCount} · 仅对比 {row.compareOnlySceneCount}
          </div>
        )}
        {row.unclassifiedSceneCount > 0 && (
          <div className="scene-set-compare-secondary">未入四象限 {row.unclassifiedSceneCount}</div>
        )}
      </div>
    ),
  },
  {
    title: '通过率',
    children: [
      ...(['today', 'compare'] as const).map((side) => ({
        title: side === 'today' ? '当前任务' : '对比任务',
        key: side, width: 105, align: 'right' as const,
        render: (_: unknown, row: Row) => {
          const metrics = row[side];
          return (
            <Tooltip title={`通过 ${metrics.passCount} / 场景 ${metrics.total}`}>
              <span>{metrics.passRate == null ? '—' : `${metrics.passRate.toFixed(2)}%`}</span>
            </Tooltip>
          );
        },
      })),
      {
        title: '差值（百分点）', key: 'delta', width: 125, align: 'right',
        render: (_, row) => {
          if (row.today.passRate == null || row.compare.passRate == null) return '—';
          const diff = Math.round((row.today.passRate - row.compare.passRate) * 100) / 100;
          return <span className={diff > 0 ? 'scene-set-compare-up' : diff < 0 ? 'scene-set-compare-down' : ''}>
            {diff > 0 ? '+' : ''}{diff.toFixed(2)}
          </span>;
        },
      },
    ],
  },
  ...matrixColumns.map(([key, mode, label, description]) => ({
    title: <><div>{label}</div><div className="scene-set-compare-secondary">{description}</div></>,
    dataIndex: key, key, width: 100, align: 'right' as const,
    render: (value: number, row: Row) => value > 0 ? (
      <Button
        type="link" className="scene-set-compare-link"
        aria-label={`${row.name} ${label} ${value} 个场景`}
        aria-pressed={selectedNodeId === row.nodeId && selectedMode === mode}
        onClick={() => onSelect(row.nodeId, mode)}
      >{value}</Button>
    ) : <span className="scene-set-compare-secondary">{value}</span>,
  })),
];

interface Props {
  nodes: SceneSetCompareNode[] | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onSelect: (nodeId: number, mode: TaskDiffMode) => void;
  selectedNodeId?: number;
  selectedMode?: TaskDiffMode;
}

export default function SceneSetComparison({ nodes, loading, error, onRetry, onSelect, selectedNodeId, selectedMode }: Props) {
  const [open, setOpen] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<readonly Key[]>([]);
  const rows = useMemo(() => toRows(nodes ?? []), [nodes]);
  const columns = makeColumns(onSelect, selectedNodeId, selectedMode);

  return (
    <Collapse
      className="scene-set-comparison"
      activeKey={open ? ['scene-sets'] : []}
      onChange={(keys) => setOpen(keys.includes('scene-sets'))}
      ghost
      items={[{
        key: 'scene-sets',
        label: '按场景集对比',
        children: (
          <>
            <p className="scene-set-compare-secondary scene-set-compare-note">
              每级汇总其全部下级场景并去重。通过率按各侧场景数计算；四象限只比较共有场景，顺序为对比任务 → 当前任务。
            </p>
            {error ? (
              <Alert
                type="error" showIcon message="场景集对比加载失败"
                action={<Button size="small" onClick={onRetry}>重试</Button>}
              />
            ) : loading || nodes == null ? (
              <div className="scene-set-compare-loading" role="status" aria-label="正在加载场景集对比"><Spin /></div>
            ) : (
              <Table<Row>
                columns={columns} dataSource={rows} rowKey="nodeId"
                size="small" pagination={false} scroll={{ x: 1160 }}
                expandable={{
                  expandedRowKeys: expandedKeys,
                  onExpandedRowsChange: setExpandedKeys,
                  indentSize: 20,
                }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无可对比的场景集" /> }}
              />
            )}
            <p className="scene-set-compare-secondary scene-set-compare-note">
              共有场景按当前任务的场景集归组，仅对比侧存在的场景使用对比侧归属。异常计为未通过，中间态不进入四象限。
              目录名称和层级按当前场景树展示；无归属、已删除或不可见的目录归入兜底行。
            </p>
          </>
        ),
      }]}
    />
  );
}
